import listingAPI from '@api/jooicez-backend/listing';
import rankingAPI from '@api/jooicez-backend/ranking';
import { EPublisherName, isRemovedListingPublisher } from '@models/listing/listing';
import { IFindListingsResponse, IFindListingsResponseElement } from '@models/listing/response';
import {
  EGridReportStatus,
  ESyncStatus,
  IGetRankingAnalyticsResponse,
  IGridReportItem,
} from '@models/location/response';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@utils/queryKeys';
import { ReactNode, createContext, useContext, useMemo, useState, useEffect } from 'react';

import { useApp } from './ApplicationContext';
import { usePolicy } from './PolicyContext';

export interface IListingContext {
  // Analytics from ApplicationContext
  rankingAnalytics: IGetRankingAnalyticsResponse | undefined;
  refetchRankingAnalytics: VoidFunction;
  isRankingAnalyticsLoading: boolean;
  hasVisibility: boolean;

  // Local grid report
  localGridReport: IGridReportItem[] | undefined;
  refetchLocalGridReport: VoidFunction;
  isLocalGridReportLoading: boolean;

  // Listings queries data
  listings: IFindListingsResponse | undefined;
  isListingsLoading: boolean;
  hasSyncedPublishers: boolean;
  refetchListings: VoidFunction;

  // Internal method to enable queries
  enableContext: VoidFunction;
}

const ListingContext = createContext<IListingContext>({
  // Analytics from ApplicationContext
  rankingAnalytics: undefined,
  refetchRankingAnalytics: () => {},
  isRankingAnalyticsLoading: false,
  hasVisibility: false,

  // Listings queries data
  listings: undefined,
  isListingsLoading: false,
  hasSyncedPublishers: false,
  refetchListings: () => {},

  // Local grid report
  localGridReport: undefined,
  refetchLocalGridReport: () => {},
  isLocalGridReportLoading: false,
  // Internal method to enable queries
  enableContext: () => {},
});

export const ListingContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [enable, setEnable] = useState(false);
  const enableContext = () => setEnable(true);

  const { locationId } = useApp();

  const { hasListing, hasListingPro } = usePolicy();

  // Listings query
  const {
    data: listingsData,
    isFetching: isListingsLoading,
    refetch: refetchListings,
  } = useQuery({
    queryKey: [queryKeys.listing.full, locationId],
    queryFn: () => listingAPI.findListing(locationId ?? 0),
    enabled: !!locationId && hasListing && enable,
    refetchOnWindowFocus: false,
    refetchInterval: 3 * 60 * 1000, // 5 minutes
  });

  //ranking analytics
  const {
    data: rankingAnalyticsData,
    isFetching: isRankingAnalyticsLoading,
    refetch: refetchRankingAnalytics,
  } = useQuery({
    queryKey: [queryKeys.rankingAnalytics.full, locationId],
    queryFn: () => rankingAPI.getRankingAnalytics(locationId ?? 0),
    enabled: !!locationId && hasListingPro && enable,
    refetchOnWindowFocus: false,
    retry: false,
  });

  //local grid report
  const {
    data: localGridReportData,
    isFetching: isLocalGridReportLoading,
    refetch: refetchLocalGridReport,
  } = useQuery({
    queryKey: [...queryKeys.localGridReport.full, locationId],
    queryFn: () => rankingAPI.getLocalGridReport(locationId ?? 0),
    enabled: !!locationId && hasListingPro,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) => {
      if (query.state.data?.data?.[0]?.status === EGridReportStatus.PROCESSING) {
        return 15000; // 15 seconds
      }
      return false;
    },
  });

  // Process listings data according to business rules
  const processListingsData = (rawListings: IFindListingsResponse | undefined): IFindListingsResponse | undefined => {
    if (!rawListings) return undefined;

    // The backend still returns the publishers Synup dropped on listings that were
    // synced before the catalog changed: they must not reach the UI at all, so they
    // are filtered out before any of the rules below run.
    const activeCatalogListings = rawListings.filter((listing) => !isRemovedListingPublisher(listing.site.name));

    // Create a copy to avoid mutating original data
    const processedListings = activeCatalogListings.map((listing): IFindListingsResponseElement => {
      const processed = { ...listing };

      // Rule 0 - Google Maps is considered synced if it has a listingUrl
      if (processed.site.name === EPublisherName.GOOGLE && processed.listingUrl) {
        processed.syncStatus = ESyncStatus.SYNCED;
        processed.syncIssue = null;
        processed.displayStatus = 'Synced';
        return processed;
      }

      // Rule 1 - Treat all IN_PROGRESS listings as synced
      if (processed.syncStatus === ESyncStatus.IN_PROGRESS) {
        processed.syncStatus = ESyncStatus.SYNCED;
      }

      // Rule 2 - Excluding DISABLED listings, when syncIssue is not null or empty set status to FAILED
      if (processed.syncStatus !== ESyncStatus.DISABLED && processed.syncIssue !== null && processed.syncIssue !== '') {
        processed.syncStatus = ESyncStatus.FAILED;
      }

      return processed;
    });

    // Rule 3 - Facebook and Instagram are considered synced if both are synced
    const facebookListing = processedListings.find((listing) => listing.site.name === EPublisherName.FACEBOOK);
    const instagramListing = processedListings.find(
      (listing) => listing.site.name === EPublisherName.INSTAGRAM && listing.site.url === 'instagram.com',
    );

    if (instagramListing && facebookListing && facebookListing.syncStatus !== ESyncStatus.DISABLED) {
      const instagramIndex = processedListings.findIndex(
        (listing) => listing.site.name === EPublisherName.INSTAGRAM && listing.site.url === 'instagram.com',
      );

      if (instagramIndex !== -1) {
        processedListings[instagramIndex] = {
          ...processedListings[instagramIndex],
          syncStatus: ESyncStatus.SYNCED,
          displayStatus: 'Sincronizzato',
        };
      }
    }

    return processedListings;
  };

  const listings = useMemo(() => processListingsData(listingsData?.data), [listingsData]);
  const rankingAnalytics = useMemo(() => rankingAnalyticsData?.data, [rankingAnalyticsData]);
  const localGridReport = useMemo(() => localGridReportData?.data, [localGridReportData]);

  const hasVisibility = useMemo(() => {
    return (
      (hasListingPro && rankingAnalytics && Array.isArray(rankingAnalytics) && rankingAnalytics.length > 0) || false
    );
  }, [hasListingPro, rankingAnalytics]);

  const hasSyncedPublishers = useMemo(() => {
    return !!listings?.some((listing) => listing.syncStatus !== ESyncStatus.DISABLED);
  }, [listings]);

  return (
    <ListingContext.Provider
      value={{
        // Analytics from ApplicationContext
        rankingAnalytics,
        refetchRankingAnalytics,
        isRankingAnalyticsLoading,
        hasVisibility,

        // Listings queries data
        hasSyncedPublishers,
        listings,
        isListingsLoading,
        refetchListings,

        // Local grid report
        localGridReport,
        refetchLocalGridReport,
        isLocalGridReportLoading,
        enableContext,
      }}
    >
      {children}
    </ListingContext.Provider>
  );
};

export const useListing = () => {
  const context = useContext(ListingContext);
  if (context === undefined) {
    throw new Error('useListing must be used within a ListingContextProvider');
  }

  // Enable queries on first use
  useEffect(() => {
    context.enableContext();
  }, [context]);

  return context;
};
